import {
  Box,
  Button,
  chakra,
  Container,
  Stack,
  Text,
  Input,
  Flex,
  Spacer,
  HStack,
  VStack,
  Image,
  useBreakpointValue,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
} from "@chakra-ui/react";
import FooterMenu from "./footermenu";
import SocialButton from "../../components/button/SocialButton";
import ListHeader from "../../components/ListHeader/ListHeader";
import {
  IconIG,
  IconLI,
  IconYT,
  IconFB,
  IconTwitter,
  IconTrustPilot,
} from "../../../styles/icons";
import Link from "next/link";
import { useCallback, useState } from "react";
import { post } from "axios";
import { markNewsletterSubscribed } from "../../articles/ArticleNewsletterModal";

export default function FooterMain() {
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [email, setEmail] = useState("");
  const [error, setError] = useState(null);

  const mobileView = useBreakpointValue(
    {
      base: true,
      xl: false,
    },
    {
      // Breakpoint to use when mediaqueries cannot be used, such as in server-side rendering
      // (Defaults to 'base')
      fallback: "base",
    }
  );

  const renderTrustPilotStars = () => {
    const stars = [];
    for (let i = 0; i < 5; i++) {
      stars.push(
        <Box
          key={i}
          bg="green.500"
          align="center"
          justify="center"
          p={1}
          m={0.5}
        >
          <IconTrustPilot color="#ffffff" size={18} p={1} />
        </Box>
      );
    }
    return stars;
  };

  const submitEmail = useCallback(async () => {
    if (submitted || submitting) {
      return;
    }

    setError(null);
    if (!email.length) {
      setError("Please enter an email");
      return;
    }

    setSubmitting(true);
    try {
      await post(`${process.env.NEXT_PUBLIC_NEW_EMAIL_URL}/list`, {
        email,
      });
      markNewsletterSubscribed();
      setSubmitted(true);
    } catch (e) {
      console.error(e);
      setError("Something went wrong, please try again");
    } finally {
      setSubmitting(false);
    }
  }, [submitted, submitting, email]);

  return (
    <chakra.footer bg="brand.200">
      <Box
        bg="white"
        width="100%"
        padding={{ base: "20px", md: "50px 0px" }}
        justifyContent="center"
        alignItems="center"
        backgroundColor="primary.500"
      >
        <Text
          textStyle="h6"
          fontWeight="bold"
          color="brand.50"
          textAlign="center"
        >
          If you need emergency help or are thinking about harming yourself,
          contact the Samaritans on 116 123.
          <br /> For emergency services phone 999 or 112.
        </Text>
      </Box>

      <Container
        maxW={{ base: "100%", md: "container.xl" }}
        bg="brand.200"
        py={10}
        px={4}
        display={{ base: "block", lg: "block" }}
      >
        <Flex direction="column" bg="brand.200" color="#ffffff">
          <Flex direction={{ base: "column", md: "row" }}>
            <Flex direction="column">
              {/*Logo*/}
              <Box>
                <Link href="/" passHref prefetch={false}>
                  <Image
                    width={{ base: "150px", lg: "247px" }}
                    height={{ base: "28px", lg: "46px" }}
                    src="/images/homepage/wd-logo.png"
                    alt="welldoing logo"
                  />
                </Link>
              </Box>

              {submitted ? (
                <span className="pt-8 font-bold text-white">
                  You&apos;re all set for our newsletter!
                </span>
              ) : (
                <>
                  <Text textStyle="footer1" pt="8">
                    Join over 30,000 on our newsletter
                  </Text>
                  <div className="flex items-center justify-start pt-2">
                    <Input
                      w={{ base: "80%", md: "300px" }}
                      h="44px"
                      borderRadius="10"
                      borderRightRadius="0"
                      placeholder="Enter email..."
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      isError={error !== null}
                    />
                    <Button
                      mx={0}
                      height="44px"
                      borderLeftRadius={0}
                      borderRightRadius={10}
                      onClick={submitEmail}
                      isLoading={submitting}
                    >
                      Subscribe
                    </Button>
                  </div>
                  {error && <span className="text-red-500">{error}</span>}
                </>
              )}
              <Spacer />
            </Flex>
            <Spacer />
            {/* Footer Links*/}
            <Flex direction="column" px={{ base: "0px", md: "50px" }}>
              {mobileView ? (
                <Accordion
                  allowToggle
                  _expanded={{ bg: "brand.100", color: "white" }}
                >
                  {FooterMenu.map((item, idx) => (
                    <AccordionItem key={idx}>
                      <AccordionButton>
                        <Box as="span" flex="1" textAlign="left">
                          {item.header}
                        </Box>

                        <AccordionIcon />
                      </AccordionButton>
                      <AccordionPanel>
                        <VStack>
                          {item.list.map((listitem, j) => (
                            <Link
                              key={j}
                              href={listitem.href}
                              textStyle="footer1"
                              passHref
                              prefetch={false}
                            >
                              {listitem.label}
                            </Link>
                          ))}
                        </VStack>
                      </AccordionPanel>
                    </AccordionItem>
                  ))}
                </Accordion>
              ) : (
                <HStack spacing={8} align="start">
                  {FooterMenu.map((item, i) => {
                    return (
                      <VStack key={i} align="start" spacing={0}>
                        <ListHeader>{item.header}</ListHeader>
                        {item.list.map((listitem, j) => (
                          <Link key={j} href={listitem.href} prefetch={false}>
                            <Text
                              textStyle="footer1"
                              style={{
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                              }}
                            >
                              {listitem.label}
                            </Text>
                          </Link>
                        ))}
                      </VStack>
                    );
                  })}
                </HStack>
              )}
            </Flex>
          </Flex>
          <Flex
            direction={{ base: "column", md: "row" }}
            alignItems={{ base: "flex-start", md: "flex-end" }}
          >
            <Flex direction="column" alignItems="start" justify="flex-start">
              {/* <Text py={{ base: "20px", md: "4px" }} textStyle="footer1">
                Check out our reviews
              </Text>
              <Flex direction="row" align="center" justify="flex-start">
                <IconTrustPilot color="#22A56E" size={20} />

                <Text textStyle="footer1" px={2}>
                  Trustpilot
                </Text>
                {renderTrustPilotStars()}
              </Flex> */}
              <Box display={{ base: "none", md: "block" }}>
                <Text fontSize="xs" pt={6} color="brand.50">
                  © 2013-25 by Welldoing. All Rights reserved.&nbsp;
                  {/* <Link
                    href="/privacy-policy#:~:text=Complaining"
                    prefetch={false}
                    rel="noopener"
                  >
                    Cookie Policy
                  </Link> */}
                  <a href="/privacy-policy#:~:text=23.Cookies" rel="noopener">
                    Cookie Policy
                  </a>
                  &nbsp;|&nbsp;
                  <Link href="/privacy-policy" prefetch={false}>
                    Privacy Policy
                  </Link>
                  &nbsp;|&nbsp;
                  <Link href="/terms-and-conditions" prefetch={false}>
                    Terms and conditions
                  </Link>
                </Text>
              </Box>
            </Flex>
            <Spacer />
            <Flex
              direction="column"
              alignItems={{ base: "flex-start", md: "flex-end" }}
            >
              <Stack direction={"row"} spacing={6} py={6}>
                <SocialButton
                  label={"Visit Welldoing on X"}
                  href={"https://www.twitter.com/welldoing_org"}
                >
                  <IconTwitter color="#836B5D" size={20} />
                </SocialButton>
                <SocialButton
                  label={"Visit Welldoing on Facebook"}
                  href={"https://www.facebook.com/welldoing.org"}
                >
                  <IconFB color="#836B5D" size={20} />
                </SocialButton>
                <SocialButton
                  label={"Visit Welldoing on YouTube"}
                  href={
                    "https://www.youtube.com/channel/UCSPVbCC-54ikaig0PpwGoSQ"
                  }
                >
                  <IconYT color="#836B5D" size={20} />
                </SocialButton>
                <SocialButton
                  label={"Visit Welldoing on LinkedIn"}
                  href={"https://www.linkedin.com/company/welldoing-org"}
                >
                  <IconLI color="#836B5D" size={20} />
                </SocialButton>
                <SocialButton
                  label={"Visit Welldoing on Instagram"}
                  href={"https://www.instagram.com/welldoinguk"}
                >
                  <IconIG color="#836B5D" size={20} />
                </SocialButton>
              </Stack>
              <Box display={{ base: "block", md: "none" }}>
                <Text fontSize="xs" pt={6} color="brand.50">
                  © 2013-25 by Welldoing. All Rights reserved. Cookie Policy |
                  Privacy Policy | Terms and conditions
                </Text>
              </Box>
              <Text
                fontSize="xs"
                color="brand.50"
                py={{ base: "8px", md: "none" }}
              >
                Welldoing Ltd is a registered trademark in England and Wales. No
                8614689.
              </Text>
            </Flex>
          </Flex>
        </Flex>
      </Container>
    </chakra.footer>
  );
}
